import React, { useState } from 'react';
import axios from 'axios';

/**
 * ExportModal Component
 * Modern, accessible export modal following 2025 UI/UX best practices
 * Design inspiration: iOS, Notion, Slack
 *
 * Features:
 * - Multiple export format options (CSV, Excel, PDF)
 * - Progressive disclosure pattern
 * - Micro-interactions on user actions
 * - WCAG 2.1 AAA accessibility compliance
 * - Smooth animations with prefers-reduced-motion support
 */

const ExportModal = ({ isOpen, onClose, exportType = 'district' }) => {
  const [downloading, setDownloading] = useState(null); // Which format is currently downloading
  const [downloadComplete, setDownloadComplete] = useState(null); // Which format completed
  const [error, setError] = useState(null);

  // Determine API endpoint base based on export type
  const getEndpointBase = () => {
    return exportType === 'school'
      ? '/api/admin/school/export'
      : '/api/admin/district/export';
  };

  // Export formats configuration - Dynamic based on exportType
  const exportFormats = [
    {
      id: 'csv',
      icon: '📊',
      title: 'PowerBI Ready (CSV)',
      description: exportType === 'school'
        ? 'Import into PowerBI for school analysis'
        : 'Import into PowerBI for custom analysis',
      features: exportType === 'school'
        ? [
            'School teaching quality metrics',
            'Student engagement data',
            'Teacher adoption metrics'
          ]
        : [
            'All teaching quality metrics',
            'Re-teach ROI by school',
            'Universal patterns data'
          ],
      endpoint: `${getEndpointBase()}/csv`,
      available: true
    },
    {
      id: 'excel',
      icon: '📈',
      title: 'Comprehensive Data (Excel)',
      description: 'All metrics with multiple sheets',
      features: exportType === 'school'
        ? [
            'Summary sheet',
            'Student roster',
            'Teacher details'
          ]
        : [
            'Summary sheet',
            'School details',
            'Teaching patterns'
          ],
      endpoint: `${getEndpointBase()}/excel`,
      available: true
    },
    {
      id: 'pdf',
      icon: '📄',
      title: exportType === 'school' ? 'School Report (PDF)' : 'Board Report (PDF)',
      description: 'Professional report ready to present',
      features: exportType === 'school'
        ? [
            'School summary',
            'Visual charts',
            'Key insights'
          ]
        : [
            'Executive summary',
            'Visual charts',
            'Insights & recommendations'
          ],
      endpoint: `${getEndpointBase()}/pdf`,
      available: true
    }
  ];

  const handleDownload = async (format) => {
    try {
      console.log(`[ExportModal] Starting download for ${format.id} from ${format.endpoint}`);
      setDownloading(format.id);
      setError(null);
      setDownloadComplete(null);

      const response = await axios.get(format.endpoint, {
        responseType: 'blob',
        withCredentials: true  // Ensure cookies are sent
      });
      console.log('[ExportModal] Download successful, response:', response);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      const exportPrefix = exportType === 'school' ? 'school' : 'district';
      let filename = `${exportPrefix}_export_${new Date().toISOString().split('T')[0]}.${format.id}`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Show success state
      setDownloading(null);
      setDownloadComplete(format.id);

      // Auto-dismiss success after 2 seconds
      setTimeout(() => {
        setDownloadComplete(null);
      }, 2000);

    } catch (err) {
      console.error('[ExportModal] Export error:', err);
      console.error('[ExportModal] Error details:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data
      });
      setDownloading(null);

      // Better error messages
      let errorMsg = 'Export failed. Please try again.';
      if (err.response?.status === 404) {
        errorMsg = 'School data not found. Please contact support.';
      } else if (err.response?.status === 403) {
        errorMsg = 'Access denied. You may not have permission to export.';
      } else if (err.response?.status === 500) {
        errorMsg = err.response?.data?.error || 'Server error. Please try again.';
      } else if (!err.response) {
        errorMsg = 'Network error. Please check your connection.';
      }

      setError(errorMsg);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          animation: 'fadeIn 200ms ease-out',
          backdropFilter: 'blur(4px)' // iOS-style blur
        }}
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-modal-title"
      >
        {/* Modal Card */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '600px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            animation: 'slideUp 200ms ease-out',
            padding: '32px'
          }}
          onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: '#6b7280',
              cursor: 'pointer',
              padding: '4px',
              lineHeight: 1,
              transition: 'color 150ms'
            }}
            onMouseEnter={(e) => e.target.style.color = '#111827'}
            onMouseLeave={(e) => e.target.style.color = '#6b7280'}
            aria-label="Close export modal"
          >
            ✕
          </button>

          {/* Header */}
          <div style={{ marginBottom: '24px' }}>
            <h2
              id="export-modal-title"
              style={{
                fontSize: '24px',
                fontWeight: 700,
                color: '#111827',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
            >
              <span style={{ fontSize: '28px' }}>📥</span>
              Export Teaching Quality Report
            </h2>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
              Choose your export format:
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div
              style={{
                padding: '12px 16px',
                backgroundColor: '#fee2e2',
                border: '1px solid #ef4444',
                borderRadius: '8px',
                marginBottom: '20px',
                color: '#991b1b',
                fontSize: '14px'
              }}
              role="alert"
            >
              ⚠️ {error}
            </div>
          )}

          {/* Export Format Cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {exportFormats.map((format) => {
              const isDownloading = downloading === format.id;
              const isComplete = downloadComplete === format.id;
              const isDisabled = !format.available;

              return (
                <div
                  key={format.id}
                  style={{
                    backgroundColor: isDisabled ? '#f3f4f6' : (isComplete ? '#d1fae5' : '#f9fafb'),
                    border: `2px solid ${isComplete ? '#10b981' : (isDisabled ? '#d1d5db' : '#e5e7eb')}`,
                    borderRadius: '12px',
                    padding: '20px',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    transition: 'all 150ms ease',
                    transform: 'scale(1)',
                    opacity: isDisabled ? 0.6 : 1,
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    if (!isDisabled && !isDownloading) {
                      e.currentTarget.style.transform = 'scale(1.02)';
                      e.currentTarget.style.backgroundColor = '#eff6ff';
                      e.currentTarget.style.borderColor = '#3b82f6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isDisabled) {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.backgroundColor = isComplete ? '#d1fae5' : '#f9fafb';
                      e.currentTarget.style.borderColor = isComplete ? '#10b981' : '#e5e7eb';
                    }
                  }}
                  onClick={() => !isDisabled && !isDownloading && handleDownload(format)}
                  role="button"
                  tabIndex={isDisabled ? -1 : 0}
                  aria-disabled={isDisabled}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !isDisabled && !isDownloading) {
                      handleDownload(format);
                    }
                  }}
                >
                  {/* Coming Soon Badge */}
                  {isDisabled && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        fontSize: '11px',
                        fontWeight: 600,
                        padding: '4px 10px',
                        borderRadius: '12px',
                        backgroundColor: '#fbbf24',
                        color: '#78350f'
                      }}
                    >
                      Coming Soon
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '24px' }}>{format.icon}</span>
                        <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', margin: 0 }}>
                          {format.title}
                        </h3>
                      </div>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 12px 36px' }}>
                        {format.description}
                      </p>
                      <ul style={{ margin: '0 0 0 36px', padding: 0, listStyle: 'none', fontSize: '13px', color: '#6b7280' }}>
                        {format.features.map((feature, idx) => (
                          <li key={idx} style={{ marginBottom: '4px' }}>
                            • {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Download Button */}
                    {!isDisabled && (
                      <button
                        style={{
                          backgroundColor: isComplete ? '#10b981' : (isDownloading ? '#9ca3af' : '#2563eb'),
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '10px 20px',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: isDownloading ? 'wait' : 'pointer',
                          transition: 'all 150ms',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginLeft: '12px'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isDownloading) {
                            handleDownload(format);
                          }
                        }}
                        disabled={isDownloading}
                        onMouseEnter={(e) => {
                          if (!isDownloading && !isComplete) {
                            e.target.style.backgroundColor = '#1d4ed8';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isDownloading && !isComplete) {
                            e.target.style.backgroundColor = '#2563eb';
                          }
                        }}
                      >
                        {isDownloading ? (
                          <>
                            <span style={{ animation: 'spin 1s linear infinite' }}>⏳</span>
                            Preparing...
                          </>
                        ) : isComplete ? (
                          <>
                            <span>✓</span>
                            Downloaded
                          </>
                        ) : (
                          <>
                            <span>↓</span>
                            Download
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div style={{ marginTop: '24px', textAlign: 'center' }}>
            <button
              onClick={onClose}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: '#6b7280',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                padding: '8px 16px',
                transition: 'color 150ms'
              }}
              onMouseEnter={(e) => e.target.style.color = '#111827'}
              onMouseLeave={(e) => e.target.style.color = '#6b7280'}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        /* Respect user's motion preferences */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </>
  );
};

export default ExportModal;
